import DOMPurify from 'dompurify';
import type { Config } from 'dompurify';
import { Fragment, type ReactNode } from 'react';
import { Link } from 'react-router-dom';

const PURIFY_CONFIG: Config = {
  ALLOWED_TAGS: [
    'p',
    'br',
    'strong',
    'b',
    'em',
    'i',
    'u',
    's',
    'strike',
    'del',
    'ul',
    'ol',
    'li',
    'h1',
    'h2',
    'h3',
    'blockquote',
    'pre',
    'code',
    'span',
    'a',
    'mark',
  ],
  ALLOWED_ATTR: ['class', 'style', 'href', 'target', 'rel'],
};

export const SEARCH_HIGHLIGHT_CLASS = 'rounded-sm bg-amber-200/90 px-0.5 text-inherit';

const escapeRegExp = (value: string) => value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

export function highlightPlainText(text: string, query?: string): ReactNode {
  const normalizedQuery = query?.trim();
  if (!normalizedQuery || !text) return text;

  const regex = new RegExp(`(${escapeRegExp(normalizedQuery)})`, 'gi');
  const parts = text.split(regex);
  const queryLower = normalizedQuery.toLowerCase();

  return parts.map((part, index) => {
    if (!part) return null;
    if (part.toLowerCase() === queryLower) {
      return (
        <mark key={index} className={SEARCH_HIGHLIGHT_CLASS}>
          {part}
        </mark>
      );
    }
    return <Fragment key={index}>{part}</Fragment>;
  });
}

export function highlightSearchInHtml(html: string, query?: string): string {
  const normalizedQuery = query?.trim();
  if (!normalizedQuery || !html) return html;
  if (typeof document === 'undefined') return html;

  const doc = new DOMParser().parseFromString(html, 'text/html');
  const regex = new RegExp(escapeRegExp(normalizedQuery), 'gi');
  const walker = doc.createTreeWalker(doc.body, NodeFilter.SHOW_TEXT);
  const textNodes: Text[] = [];

  while (walker.nextNode()) {
    textNodes.push(walker.currentNode as Text);
  }

  textNodes.forEach((node) => {
    const text = node.textContent ?? '';
    regex.lastIndex = 0;
    if (!regex.test(text)) return;

    regex.lastIndex = 0;
    const fragment = doc.createDocumentFragment();
    let lastIndex = 0;
    let match: RegExpExecArray | null;

    while ((match = regex.exec(text)) !== null) {
      if (match.index > lastIndex) {
        fragment.appendChild(doc.createTextNode(text.slice(lastIndex, match.index)));
      }

      const mark = doc.createElement('mark');
      mark.className = SEARCH_HIGHLIGHT_CLASS;
      mark.textContent = match[0];
      fragment.appendChild(mark);
      lastIndex = match.index + match[0].length;
    }

    if (lastIndex < text.length) {
      fragment.appendChild(doc.createTextNode(text.slice(lastIndex)));
    }

    node.parentNode?.replaceChild(fragment, node);
  });

  return doc.body.innerHTML;
}

export function looksLikeHtmlMessage(text: string): boolean {
  const t = text.trim();
  if (!t.startsWith('<')) return false;
  return /<\/[a-z][\w-]*>/i.test(t) || /^<\s*br\b/i.test(t);
}

function stripHtmlTags(html: string): string {
  let out = '';
  let i = 0;
  while (i < html.length) {
    const lt = html.indexOf('<', i);
    if (lt === -1) {
      out += html.slice(i);
      break;
    }
    out += html.slice(i, lt);
    const gt = html.indexOf('>', lt + 1);
    if (gt === -1) {
      out += html.slice(lt);
      break;
    }
    if (gt > lt + 1) {
      out += ' ';
      i = gt + 1;
    } else {
      out += html[lt];
      i = lt + 1;
    }
  }
  return out;
}

export function getPlainTextFromHtml(html: string): string {
  if (typeof document === 'undefined') {
    return stripHtmlTags(html).replace(/\s+/g, ' ').trim();
  }
  const doc = new DOMParser().parseFromString(html, 'text/html');
  return (doc.body.textContent || '').replace(/\u200b/g, '').trim();
}

export const MESSAGE_LINK_LABEL = 'Подробнее';

/** Normalize portal/HTML anchors to a single "Подробнее" link on its own line. */
export function normalizeMessageHtmlLinks(html: string): string {
  if (!html || typeof document === 'undefined') return html;

  const doc = new DOMParser().parseFromString(html, 'text/html');
  const anchors = Array.from(doc.body.querySelectorAll('a[href]'));

  anchors.forEach((anchor) => {
    anchor.textContent = MESSAGE_LINK_LABEL;
    anchor.setAttribute('rel', 'noopener noreferrer');
    if (!anchor.getAttribute('target')) {
      anchor.setAttribute('target', '_blank');
    }

    if (anchor.parentElement?.tagName === 'DIV' && anchor.parentElement.childNodes.length === 1) {
      return;
    }

    const wrapper = doc.createElement('div');
    anchor.parentNode?.insertBefore(wrapper, anchor);
    wrapper.appendChild(anchor);
  });

  return doc.body.innerHTML;
}

export function sanitizeMessageHtml(html: string): string {
  return normalizeMessageHtmlLinks(DOMPurify.sanitize(html, PURIFY_CONFIG));
}

export function hasVisibleMessageText(text: string): boolean {
  const plain = looksLikeHtmlMessage(text) ? getPlainTextFromHtml(text) : text;
  return plain.replace(/\u00a0/g, ' ').trim().length > 0;
}

const LINKS_DETECT_REGEX = /(\$[^(\r\n]+\(https?:\/\/[^)\s]+\)|https?:\/\/\S+)/; // NOSONAR
const LINK_TEXTED_REGEX = /^\$([^(\r\n]+)\((https?:\/\/[^)\s]+)\)$/; // NOSONAR
const LINK_PLAIN_REGEX = /^https?:\/\/\S+$/; // NOSONAR

const isCurrentBaseUrl = (url: string) => {
  return url.startsWith(window.location.origin);
};

const renderMessageLink = (url: string, index: number, highlightQuery?: string) => (
  <div key={`${url}-${index}`} className="mt-1">
    <Link
      className="text-black underline"
      target={!isCurrentBaseUrl(url) ? '_blank' : undefined}
      to={url}
    >
      {highlightPlainText(MESSAGE_LINK_LABEL, highlightQuery)}
    </Link>
  </div>
);

export const replaceLinksInText = (text: string, highlightQuery?: string) => {
  const output = text.split(LINKS_DETECT_REGEX).map((item, index) => {
    if (!item) return null;

    const check = item.match(LINK_TEXTED_REGEX);

    if (check && check[1] && check[2]) {
      return renderMessageLink(check[2], index, highlightQuery);
    }

    const plainCheck = item.match(LINK_PLAIN_REGEX);

    if (plainCheck) {
      return renderMessageLink(plainCheck[0], index, highlightQuery);
    }

    return <Fragment key={index}>{highlightPlainText(item, highlightQuery)}</Fragment>;
  });

  return output;
};
